import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  fetchBunkerFreshWaterData,
  updateBunkerFreshWaterData,
  deleteBunkerData,
  addBankerFreshWaterData,
  moveBunkerData,
} from "../thunks/bunkerFreshWaterThunks";

import type { BunkerFreshWater } from "../../utils/types/bunkerFreshWater";

interface BunkerState {
  bunker: {
    [key: string]: BunkerFreshWater[] | undefined;
  } | null;
  loading: boolean;
  error: string | null;
}

const initialState: BunkerState = {
  bunker: null,
  loading: false,
  error: null,
};

const bunkerFreshWaterSlice = createSlice({
  name: "bunker",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBunkerFreshWaterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBunkerFreshWaterData.fulfilled, (state, action) => {
        state.loading = false;
        const { payload } = action;

        const { jobId } = payload;
        if (!jobId) {
          state.error = "Invalid job ID";
          return;
        }

        if (!state.bunker) {
          state.bunker = {};
        }
        state.bunker[jobId] = payload.data || [];
      })
      .addCase(fetchBunkerFreshWaterData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
    builder
      .addCase(deleteBunkerData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBunkerData.fulfilled, (state, action) => {
        state.loading = false;
        const { payload } = action;
        if (!payload || !payload.jobId || !payload?.bunkersFreshWaterId) {
          state.error = " Invalid job ID or bunker ID";
          return;
        }

        if (!state.bunker || !state.bunker[payload.jobId]) {
          return;
        }

        if (state.bunker[payload.jobId]) {
          state.bunker[payload.jobId] = state.bunker[payload.jobId]!.filter(
            (bunkerData) =>
              payload?.bunkersFreshWaterId !== bunkerData.bunkersFreshWaterId
          );
        }
      })
      .addCase(deleteBunkerData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
    builder
      .addCase(updateBunkerFreshWaterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBunkerFreshWaterData.fulfilled, (state, action) => {
        state.loading = false;
        const { payload } = action;
        if (!payload || !payload.jobId || !payload?.data.bunkersFreshWaterId) {
          state.error = " Invalid job ID or delivery vendor ID";
          return;
        }

        if (!state.bunker || !state.bunker[payload.jobId]) {
          return;
        }

        if (state.bunker[payload.jobId]) {
          state.bunker[payload.jobId] = state.bunker[payload.jobId]?.map(
            (bunkerData) => {
              if (
                bunkerData.bunkersFreshWaterId ===
                payload?.data?.bunkersFreshWaterId
              ) {
                return { ...bunkerData, ...action.payload?.data };
              }
              return bunkerData;
            }
          );
        }
      })
      .addCase(updateBunkerFreshWaterData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
    builder
      .addCase(addBankerFreshWaterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addBankerFreshWaterData.fulfilled, (state, action) => {
        state.loading = false;
        const { payload } = action;
        if (!payload) {
          return;
        }

        const jobId = payload?.jobId;
        if (!jobId) {
          state.error = "Invalid job ID in response";
          return;
        }

        if (!state.bunker) {
          state.bunker = {};
        }

        const existingDeliveries = state.bunker[jobId] || [];
        state.bunker[jobId] = [payload.data, ...existingDeliveries];
      })
      .addCase(addBankerFreshWaterData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(moveBunkerData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveBunkerData.fulfilled, (state, action) => {
        state.loading = false;
        // state.bunker = state.bunker.filter(
        //   (operation) => action.payload?.id !== operation.bunkersFreshWaterId
        // );
        const { payload } = action;
        if (!payload || !payload.jobId || !payload?.id) {
          state.error = " Invalid job ID or delivery vendor ID";
          return;
        }

        if (!state.bunker || !state.bunker[payload.jobId]) {
          return;
        }

        if (state.bunker[payload.jobId]) {
          state.bunker[payload.jobId] = state.bunker[payload.jobId]?.map(
            (bunkerData) => {
              if (bunkerData.bunkersFreshWaterId === payload?.id) {
                return {
                  ...bunkerData,
                  berthId: action.payload?.data?.berthId,
                };
              }
              return bunkerData;
            }
          );
        }
      })
      .addCase(moveBunkerData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    addWebSocketCases(builder, "bunker", "bunker");
  },
});

export default bunkerFreshWaterSlice.reducer;
